import {
  QUALITY_LEVEL,
  DATE_PUBLISHED,
  ASSET_TYPE,
  LICENSE_TYPE,
  DEFAULT_STATE,
} from './constants'
import { searchPageStore } from './index'

/**
 * Whether a print-size dimension object has at least one axis max set
 * @param {Object} dimensions - { width, depth, height } with numeric or '' values
 * @returns {boolean}
 */
export const hasAnyDimension = (dimensions) => Object.values(dimensions || {})
  .some(value => value !== '' && value !== undefined)

/**
 * Gets the count of active main filters
 * @param {Object} state - The search page store state
 * @returns {number} Number of active main filters
 */
const getMainFiltersCount = (state) => {
  let count = 0

  // Tags come from Search within results, which shows on every tab (All included), as well as from
  // the 3D / 3D print category rows, and buildSearchParams always sends them.
  if (state.tags.length > 0) {
    count += state.tags.length
  }
  if (state.fileTypes.length > 0) {
    count += state.fileTypes.length
  }
  if (state.quality.length > 0) {
    const hasNonAnyQuality = state.quality.some(q => q !== QUALITY_LEVEL.ANY)
    if (hasNonAnyQuality) {
      count += state.quality.length
    }
  }
  if (state.contentSource !== DEFAULT_STATE.contentSource) {
    count += 1
  }
  if (state.onSale === true) {
    count += 1
  }
  if (state.withSubscription === true) {
    count += 1
  }
  if (state.priceRanges.length > 0) {
    count += state.priceRanges.length
  }
  if ((state.minPrice !== DEFAULT_STATE.minPrice && state.minPrice !== 0)
    || (state.maxPrice !== DEFAULT_STATE.maxPrice && state.maxPrice !== 0)) {
    count += 1
  }
  if (
    state.datePublished !== DATE_PUBLISHED.ALL_TIME ||
    (state.minDatePublished !== undefined && state.maxDatePublished !== undefined)
  ) {
    count += 1
  }
  if (state.designers.length > 0) {
    count += state.designers.length
  }
  // A whole-scope flag stands in for its members, which only the designer options endpoint knows,
  // so it counts as one filter here.
  if (state.designerScopes.length > 0) {
    count += state.designerScopes.length
  }
  if (state.licenses.length > 0 && !state.licenses.includes(LICENSE_TYPE.ALL)) {
    count += state.licenses.length
  }
  if (state.advancedFilters.length > 0) {
    count += state.advancedFilters.length
  }
  if (state.personalFilter !== DEFAULT_STATE.personalFilter) {
    count += 1
  }
  if (state.purchaseFilter !== DEFAULT_STATE.purchaseFilter) {
    count += 1
  }
  if (state.keyword.trim() !== '') {
    count += 1
  }

  return count
}

/**
 * Gets the count of active filters for the asset type "3D Only"
 * @param {Object} state - The search page store state
 * @returns {number} Number of active filters for the asset type "3D Only"
 */
const getAssetTypeThreeDOnlyFiltersCount = (state) => {
  let count = 0

  if (state.animated === true) {
    count += 1
  }
  if (state.pbr === true) {
    count += 1
  }
  if (state.rigged === true) {
    count += 1
  }
  if (state.gameReady === true) {
    count += 1
  }
  if (state.withRenders === true) {
    count += 1
  }
  if (state.styles.length > 0) {
    count += state.styles.length
  }
  if (state.polygonRanges.length > 0) {
    count += state.polygonRanges.length
  }
  if (state.minPolygon !== undefined || state.maxPolygon !== undefined) {
    count += 1
  }

  return count
}

/**
 * Gets the count of active filters for the asset type "3D Print Only"
 * @param {Object} state - The search page store state
 * @returns {number} Number of active filters for the asset type "3D Print Only"
 */
const getAssetTypeThreeDPrintOnlyFiltersCount = (state) => {
  let count = 0

  if (state.withMakes === true) {
    count += 1
  }
  if (state.threeDPrintVerified === true) {
    count += 1
  }
  if (state.preSupported === true) {
    count += 1
  }
  if (state.multipleParts === true) {
    count += 1
  }
  if (state.styles.length > 0) {
    count += state.styles.length
  }
  if (hasAnyDimension(state.wholePrintSize)) {
    count += 1
  }
  if (hasAnyDimension(state.largestPrintSize)) {
    count += 1
  }
  if (state.sizePreset !== undefined) {
    count += 1
  }

  return count
}

/**
 * Gets the count of active secondary filters
 * @param {Object} state - The search page store state
 * @returns {number} Number of active secondary filters
 */
export const getSecondaryFiltersCount = (state) => {
  if (state.assetType === ASSET_TYPE.ALL) {
    return 0
  }

  if (state.assetType === ASSET_TYPE.THREE_D_ONLY) {
    return getAssetTypeThreeDOnlyFiltersCount(state)
  }

  if (state.assetType === ASSET_TYPE.THREE_D_PRINT_ONLY) {
    return getAssetTypeThreeDPrintOnlyFiltersCount(state)
  }

  return 0
}

/**
 * Gets the count of active filters
 * @param {Object} [state] - Optional state object. If not provided, gets state from store.
 * @returns {number} Number of active filters
 */
export const getFiltersCount = (state) => {
  const currentState = state || searchPageStore.getState()
  const mainFiltersCount = getMainFiltersCount(currentState)
  const secondaryFiltersCount = getSecondaryFiltersCount(currentState)

  return mainFiltersCount + secondaryFiltersCount
}
